import { evalAuthHeaders, getEvalBase } from '../../evalApiBase'
import {
  buildMcpMocksListQuery,
  McpMock,
  McpMockCreateInput,
  McpMockFilters,
  McpMockListResponse,
  McpMockUpdateInput,
} from './mcpMocksAdminModel'

async function readErrorDetail(res: Response): Promise<string | null> {
  try {
    const body = await res.json()
    if (body && typeof body.detail === 'string') {
      return body.detail
    }
  } catch {
    // fall through
  }
  return null
}

export async function listMcpMocks(
  filters: McpMockFilters,
): Promise<McpMockListResponse> {
  const query = buildMcpMocksListQuery(filters)
  const res = await fetch(`${getEvalBase()}/v1/eval-scenarios?${query}`, {
    headers: evalAuthHeaders(),
  })
  if (!res.ok) {
    throw new Error(`MCP mocks list failed: ${res.status}`)
  }
  return res.json()
}

export async function createMcpMock(input: McpMockCreateInput): Promise<McpMock> {
  const res = await fetch(`${getEvalBase()}/v1/eval-scenarios`, {
    method: 'POST',
    headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    // Surface the backend detail — name collisions (409) and save-time
    // validation failures (422) carry actionable messages.
    const detail = await readErrorDetail(res)
    throw new Error(detail || `MCP mock create failed: ${res.status}`)
  }
  return res.json()
}

export async function updateMcpMock(input: McpMockUpdateInput): Promise<McpMock> {
  const { scenario_id, ...payload } = input
  const res = await fetch(`${getEvalBase()}/v1/eval-scenarios/${scenario_id}`, {
    method: 'PUT',
    headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    // Same backend rules as create: name collisions (409) and the name↔scenario
    // save-time check (422) carry actionable messages worth surfacing.
    const detail = await readErrorDetail(res)
    throw new Error(detail || `MCP mock update failed: ${res.status}`)
  }
  return res.json()
}

export async function deleteMcpMock(scenarioId: string): Promise<void> {
  const res = await fetch(`${getEvalBase()}/v1/eval-scenarios/${scenarioId}`, {
    method: 'DELETE',
    headers: evalAuthHeaders(),
  })
  if (!res.ok) {
    // A mock referenced by eval cases returns 409 with a message naming the
    // count; surface it rather than a bare status code.
    const detail = await readErrorDetail(res)
    throw new Error(detail || `MCP mock delete failed: ${res.status}`)
  }
}
